import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { IStoreState } from '../../types';
import { INpsState } from '../../types/nps';
import {
  selectNpsScore,
  submitNps,
} from '../../actions/npsActions';
import NpsScoreScale from './NpsScoreScale';
import { Button } from '@orderly/morrisons-component-library';
import {
  NPS_INTRO_TEXT,
  NPS_SCORE_QUESTION,
  NPS_FEEDBACK_LABEL,
  NPS_FEEDBACK_PLACEHOLDER,
  NPS_SUBMIT_BUTTON,
  NPS_MODAL_HEADER,
} from '../../constants/npsStrings';
import './NpsModal.css';

interface INpsModalStateProps {
  nps: INpsState;
}

interface INpsModalDispatchProps {
  onScoreSelect: (score: number) => void;
  onSubmit: (npsResponseId: number, score: number, feedback: string | null) => void;
}

type INpsModalProps = INpsModalStateProps & INpsModalDispatchProps;

const NpsModal: React.FC<INpsModalProps> = ({ nps, onScoreSelect, onSubmit }) => {
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (!nps.isModalOpen) {
      setFeedback('');
    }
  }, [nps.isModalOpen]);

  const handleSubmit = useCallback(() => {
    if (nps.selectedScore === null || nps.npsResponseId === null) {
      return;
    }

    onSubmit(
      nps.npsResponseId,
      nps.selectedScore,
      feedback.trim() || null,
    );
  }, [nps.selectedScore, nps.npsResponseId, feedback, onSubmit]);

  if (!nps.isModalOpen) {
    return null;
  }

  const canSubmit = nps.selectedScore !== null && !nps.isSubmitting;

  return (
    <div className="modal-dialog-wrapper open" role="dialog" aria-modal="true" aria-label={NPS_MODAL_HEADER}>
      <div className="modal-dialog-container">
        <div className="modal-dialog-header">
          <h4>{NPS_MODAL_HEADER}</h4>
        </div>
        <div className="modal-dialog-content">
          <div className="nps-modal-wrapper">
            <p className="nps-intro-text">{NPS_INTRO_TEXT}</p>
            <p className="nps-question-text">{NPS_SCORE_QUESTION}</p>

            <NpsScoreScale
              selectedScore={nps.selectedScore}
              onScoreSelect={onScoreSelect}
            />

            {nps.selectedScore !== null && (
              <div className="nps-feedback-section">
                <label className="nps-feedback-label" htmlFor="nps-feedback">
                  {NPS_FEEDBACK_LABEL}
                </label>
                <textarea
                  id="nps-feedback"
                  className="nps-feedback-textarea"
                  placeholder={NPS_FEEDBACK_PLACEHOLDER}
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  maxLength={500}
                  aria-label="NPS feedback"
                />
              </div>
            )}

            {nps.error && (
              <p className="nps-error-message" role="alert">{nps.error}</p>
            )}
          </div>
        </div>
        <div className="modal-dialog-footer">
          <Button
            type="button"
            className="primary"
            text={nps.isSubmitting ? 'Submitting...' : NPS_SUBMIT_BUTTON}
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStoreState): INpsModalStateProps => {
  return {
    nps: (state as any).nps,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): INpsModalDispatchProps => ({
  onScoreSelect: (score: number) => dispatch(selectNpsScore(score)),
  onSubmit: (npsResponseId: number, score: number, feedback: string | null) =>
    (dispatch as any)(submitNps({ npsResponseId, score, feedback })),
});

export default connect(mapStateToProps, mapDispatchToProps)(NpsModal);
